/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Drawer } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { AlertConfig, AlertConfigRequest, AlertConfigUpdateRequest, Node } from '@/client';
import { useMessage } from '@/hooks/message';
import { useCreateAlertConfigMutation } from '@/hooks/mutation/useCreateAlertConfigMutation';
import { useUpdateAlertConfigMutation } from '@/hooks/mutation/useUpdateAlertConfigMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';

import { AlertWizardStep, DestinationType, getDisableTls, getToken, getWebhookUrl } from './constants';
import { CreateAlertWizardHeader } from './CreateAlertWizardHeader';
import { ReviewStep } from './ReviewStep';
import { SetParametersStep } from './SetParametersStep';
import { SetupConnectionStep } from './SetupConnectionStep';

import './CreateAlertWizard.css';

type EventType = 'signal' | 'deflection' | 'hallmark' | 'lifecycle';

type CreateAlertWizardProps = {
  destinationType: DestinationType;
  onClose: () => void;
  editAlert?: AlertConfig;
};

const destinationToApiType: Record<string, AlertConfigRequest['type']> = {
  slack: 'slack',
  exabeam: 'exabeam',
  splunk: 'splunk',
};

function buildConfig(
  destinationType: DestinationType,
  webhookUrl: string,
  token: string,
  disableTls: boolean = false,
): AlertConfigRequest['config'] {
  switch (destinationType) {
    case 'slack':
      return { slackWebhook: webhookUrl };
    case 'exabeam':
      return { exabeamWebhook: webhookUrl, exabeamToken: token };
    case 'splunk':
      return { splunkWebhook: webhookUrl, splunkToken: token, disableTlsVerification: disableTls };
    default:
      return { slackWebhook: webhookUrl };
  }
}

export function CreateAlertWizard({ destinationType, onClose, editAlert }: CreateAlertWizardProps) {
  const { t } = useMimicTranslation('notificationManagement');
  const [messageApi] = useMessage();
  const isEditMode = !!editAlert;
  const [currentStep, setCurrentStep] = useState<AlertWizardStep>('setup-connection');

  const [name, setName] = useState(editAlert?.name ?? '');
  const [description, setDescription] = useState(editAlert?.description ?? '');
  const [webhookUrl, setWebhookUrl] = useState(editAlert ? getWebhookUrl(editAlert) : '');
  const [token, setToken] = useState(editAlert ? getToken(editAlert) : '');
  const [disableTls, setDisableTls] = useState(editAlert ? getDisableTls(editAlert) : false);
  const [eventTypes, setEventTypes] = useState<EventType[]>(
    editAlert?.eventTypes?.filter((et): et is EventType =>
      ['signal', 'deflection', 'hallmark', 'lifecycle'].includes(et),
    ) ?? [],
  );
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);
  const [includeFilters, setIncludeFilters] = useState<Record<string, string>>(
    editAlert?.keyValueFilters?.include
      ? Object.fromEntries(Object.entries(editAlert.keyValueFilters.include).map(([k, v]) => [k, String(v)]))
      : {},
  );
  const [excludeFilters, setExcludeFilters] = useState<Record<string, string>>(
    editAlert?.keyValueFilters?.exclude
      ? Object.fromEntries(Object.entries(editAlert.keyValueFilters.exclude).map(([k, v]) => [k, String(v)]))
      : {},
  );
  const [selectedTags, setSelectedTags] = useState<string[]>(editAlert?.tags ?? []);
  const [nodesInitialized, setNodesInitialized] = useState(false);

  const nodeMap = useMemo(() => {
    if (!editAlert?.nodeDetails) return new Map<string, string>();
    return new Map(editAlert.nodeDetails.map((n: { id: string; name: string }) => [n.id, n.name]));
  }, [editAlert?.nodeDetails]);

  useEffect(() => {
    if (!editAlert?.nodeIds?.length || nodesInitialized) return;
    if (nodeMap.size === 0) return;
    const resolved = editAlert.nodeIds.map((id) => ({ id, name: nodeMap.get(id) || id }) as unknown as Node);
    setSelectedNodes(resolved);
    setNodesInitialized(true);
  }, [nodeMap, editAlert, nodesInitialized]);

  const { mutate: createAlert, isPending: isCreating } = useCreateAlertConfigMutation({
    onSuccess: () => {
      messageApi.success(t('createSuccess'));
      onClose();
    },
    onError: () => {
      messageApi.error(t('createError'));
    },
  });

  const { mutate: updateAlert, isPending: isUpdating } = useUpdateAlertConfigMutation({
    onSuccess: () => {
      messageApi.success(t('details.configUpdated'));
      onClose();
    },
    onError: () => {
      messageApi.error(t('details.configUpdateError'));
    },
  });

  const isPending = isCreating || isUpdating;

  const handleSubmit = () => {
    const apiType = destinationToApiType[destinationType];
    if (!apiType) {
      messageApi.error(t('unsupportedType'));
      return;
    }

    if (isEditMode && editAlert) {
      const requestBody: AlertConfigUpdateRequest = {
        config: buildConfig(destinationType, webhookUrl, token, disableTls),
        name,
        description: description || undefined,
        eventTypes: eventTypes as AlertConfigUpdateRequest['eventTypes'],
        nodeIds: selectedNodes.map((n) => n.id),
        keyValueFilters: {
          include: includeFilters,
          exclude: excludeFilters,
        },
        tags: selectedTags,
      };
      updateAlert({ id: editAlert.id, requestBody });
    } else {
      const config = buildConfig(destinationType, webhookUrl, token, disableTls);
      const requestBody = {
        type: apiType,
        config,
        name,
        description: description || undefined,
        eventTypes: eventTypes as AlertConfigRequest['eventTypes'],
        nodeIds: selectedNodes.map((n) => n.id),
        keyValueFilters: {
          include: includeFilters,
          exclude: excludeFilters,
        },
        status: 'active' as const,
        tags: selectedTags,
      } as AlertConfigRequest;
      createAlert(requestBody);
    }
  };

  const steps = {
    'setup-connection': (
      <SetupConnectionStep
        destinationType={destinationType}
        name={name}
        description={description}
        webhookUrl={webhookUrl}
        token={token}
        disableTls={disableTls}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onWebhookUrlChange={setWebhookUrl}
        onTokenChange={setToken}
        onDisableTlsChange={setDisableTls}
        onNext={() => setCurrentStep('set-parameters')}
        onCancel={onClose}
      />
    ),
    'set-parameters': (
      <SetParametersStep
        destinationType={destinationType}
        eventTypes={eventTypes}
        selectedNodes={selectedNodes}
        selectedTags={selectedTags}
        includeFilters={includeFilters}
        excludeFilters={excludeFilters}
        onEventTypesChange={setEventTypes}
        onSelectedNodesChange={setSelectedNodes}
        onSelectedTagsChange={setSelectedTags}
        onIncludeFiltersChange={setIncludeFilters}
        onExcludeFiltersChange={setExcludeFilters}
        onNext={() => setCurrentStep('review')}
        onPrevious={() => setCurrentStep('setup-connection')}
      />
    ),
    review: (
      <ReviewStep
        destinationType={destinationType}
        name={name}
        description={description}
        webhookUrl={webhookUrl}
        eventTypes={eventTypes}
        selectedNodes={selectedNodes}
        selectedTags={selectedTags}
        includeFilters={includeFilters}
        excludeFilters={excludeFilters}
        onPrevious={() => setCurrentStep('set-parameters')}
        onSubmit={handleSubmit}
        loading={isPending}
        isEditMode={isEditMode}
      />
    ),
  };

  return (
    <Drawer onClose={onClose} className="create-alert-wizard" placement="top" open height="100vh" closeIcon={null}>
      <div className="create-alert-wizard-container">
        <CreateAlertWizardHeader currentStep={currentStep} onClose={onClose} />
        {steps[currentStep]}
      </div>
    </Drawer>
  );
}
